import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSame from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
// @ts-ignore
import { createBrowserHistory } from 'history';
import { Agent, AppCopyText, AppNotification, TeamHoliday } from 'typings';

import verseIcon from 'assets/images/logo-v2.png';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(isSame);
dayjs.extend(isSameOrAfter);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const browserHistory = createBrowserHistory();

export const apiErrorNotification = (
  error: any,
  overrideNonServerErrorMessage?: string
): AppNotification => {
  const statusCode = error?.error?.statusCode ?? 500;
  let message;

  if (statusCode >= 500) {
    message = error?.error?.systemError;
  }

  // use the standard error message if the provided response can't be used
  if (!message) {
    message =
      overrideNonServerErrorMessage ??
      error?.error?.data?.message ?? // Core API pattern
      AppCopyText.ACTION_ERROR_MESSAGE;
  }

  return { text: message, type: 'error' };
};

export const goToHomePage = () => {
  browserHistory.push('/');
};

export const formatPhoneNumber = (
  phoneNumber = '',
  countryCode = 'US',
  format?: PhoneNumberFormat
): string => {
  if (!phoneNumber) {
    return phoneNumber;
  }

  if (typeof phoneNumber === 'number') {
    phoneNumber = `${phoneNumber}`;
  }

  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parseAndKeepRawInput(phoneNumber, countryCode);

    return phoneUtil.format(number, format || PhoneNumberFormat.NATIONAL);
  } catch (e) {
    return phoneNumber;
  }
};

export const getTimeDifference = (start: string, end?: string | null) => {
  const createdAt = dayjs.utc(start);
  if (end) {
    const endedAt = dayjs.utc(end);

    // endedAt is before createdAt
    if (endedAt.diff(createdAt) < 0) {
      return 'Unknown';
    }

    return dayjs.utc(endedAt.diff(createdAt)).format('HH:mm:ss');
  }

  return 'Call in progress';
};

export const unixTimestamp = (dt: string, timezone: string) => {
  try {
    return dayjs.tz(dt, timezone).unix();
  } catch (err) {
    return null;
  }
};

export const formatDate = (dt: string | number, fmt: string, timezone?: string) => {
  try {
    if (!timezone) {
      return dayjs.utc(dt).local().format(fmt);
    }

    return dayjs(new Date(dt).toLocaleString('en-US', { timeZone: timezone })).format(fmt);
  } catch (err) {
    return '';
  }
};

export const getLocalTimezone = () => dayjs.tz.guess();

export const formatTimezone = (timezone: string, dt?: string | number) => {
  try {
    const date = new Date(dt || Date.now());
    const dateWithTimezone = date.toLocaleTimeString('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });

    // format will be hh:mm:ss AM/PM TZ
    // we want just the TZ
    return dateWithTimezone.split(' ').pop() || null;
  } catch (err) {
    return '';
  }
};

export const formatAddress = (
  street: string | null = '',
  city: string | null = '',
  state: string | null = '',
  postalCode: string | null = ''
) => {
  let address = [street, city, state].filter(Boolean).join(', ');

  if (postalCode) {
    if (address) address += ' ';
    address += postalCode;
  }

  return address;
};

// front end version of what happens in console proxy (same func name)
// this value is NOT used on the backend, it is handled independently
export const buildLeadComment = (
  additionalNote: string,
  scriptResponseSummaryString: string | null = null,
  unqualifyReasonString: string | null = null
) => {
  let comment: string[] = [];

  if (unqualifyReasonString) {
    comment.push(unqualifyReasonString);
  }

  if (scriptResponseSummaryString) {
    comment.push(scriptResponseSummaryString);
  }

  if (additionalNote) {
    comment.push(`Additional Note: \n${additionalNote}`);
  }

  return comment.join('\n\n');
};

export const isTeamHoliday = (agent: Agent, dt?: string | number) => {
  const agentTimezone = agent?.agentSetting?.settings?.general?.timezone;
  const businessHours = agent?.agentSetting?.businessHours;
  const teamHolidays = agent?.team?.teamHolidays;

  if (!businessHours?.enabled || !teamHolidays || teamHolidays.length === 0) {
    return false;
  }

  const agentDate = dayjs(dt).tz(agentTimezone);

  const isHoliday = teamHolidays.some((holiday) => {
    const holidayDate = dayjs.tz(holiday.date, agentTimezone).startOf('day');
    return agentDate.isSame(holidayDate, 'day');
  });

  return isHoliday;
};

export const filteredHolidays = (holidays: TeamHoliday[] | undefined, timezone: string) => {
  // Filter for holidays starting from today, up to 5 days in the future
  const today = dayjs.tz(Date.now(), timezone).startOf('day');

  return holidays
    ?.filter((holiday) => dayjs.tz(holiday.date, timezone).isSameOrAfter(today, 'day')) // Includes today
    .sort((a, b) => {
      // Sort holidays by date, ensuring today is at the top
      const holidayA = dayjs.tz(a.date, timezone).startOf('day');
      const holidayB = dayjs.tz(b.date, timezone).startOf('day');
      return holidayA.diff(holidayB);
    })
    .slice(0, 5) // Limit to 5 holidays
    .map((holiday) => {
      const holidayDate = dayjs.tz(holiday.date, timezone);
      const formattedDate = holidayDate.isSame(today, 'day')
        ? 'Today'
        : holidayDate.format('MMM D');
      return {
        date: formattedDate,
        description: holiday.description || 'Holiday',
      };
    });
};

export const isWithinBusinessHours = (agent: Agent, dt?: string | number) => {
  const agentTimezone = agent?.agentSetting?.settings?.general?.timezone;
  const businessHours = agent?.agentSetting?.businessHours;

  if (!businessHours?.enabled) return true;

  const localTime = dayjs();
  const agentDate = localTime.tz(agentTimezone);
  const agentDow: any = agentDate.format('dddd'); // Full name of the day (e.g., "Sunday")
  const agentTime = agentDate.format('HH:mm');

  const intervals = businessHours?.hours?.find((day) => day.day === agentDow)?.intervals;

  if (!intervals || intervals.length === 0) return false;

  return intervals.some((interval) => interval.from <= agentTime && interval.to > agentTime);
};

export const isCallCenterTransferRestricted = (agent: Agent) => {
  return agent?.agentSetting?.businessHours?.restrictedActions?.callCenterTransfers ?? true;
};

export const isLiveTransferRestricted = (agent: Agent) => {
  return agent?.agentSetting?.businessHours?.restrictedActions?.liveTransfers ?? true;
};

export const capitalizeFirst = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

export const isValidPhoneNumber = (phone: string, countryCode: string = 'US'): boolean => {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();

    return phoneUtil.isValidNumberForRegion(phoneUtil.parse(phone, countryCode), countryCode);
  } catch (e) {
    return false;
  }
};

// basic check to ensure the provided URL parses correctly as http or https
export const isValidWebUrl = (url: string): boolean => {
  try {
    const validUrl = new URL(url);
    return validUrl.protocol === 'http:' || validUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

// return true if the provided URL parses correctly as https
export const isSecureWebUrl = (url: string): boolean => {
  try {
    const validUrl = new URL(url);
    return validUrl.protocol === 'https:';
  } catch {
    return false;
  }
};

export const isBrowserSupportedMediaType = (mimeType: string): boolean => {
  const match = mimeType.match(/^(image|audio|video)/);

  if (!match) return false;

  const mediaType = match[0];

  if (mediaType === 'audio') {
    const audio = new Audio();
    return !!audio.canPlayType(mimeType);
  }

  if (mediaType === 'video') {
    const video = document.createElement('video');
    return !!video.canPlayType(mimeType);
  }

  return true;
};

export const storeInLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

export const getFromLocalStorage = (key: string) => {
  return localStorage.getItem(key);
};

export const storeInSessionStorage = (key: string, value: string | any) => {
  sessionStorage.setItem(key, value);
};

export const getFromSessionStorage = (key: string) => {
  return sessionStorage.getItem(key);
};

export const checkWebNotificationPermission = (options = {}) => {
  if (!('Notification' in window)) {
    return false; // notifications not supported by browser
  }

  return Notification.permission === 'granted';
};

export const requestWebNotificationPermission = () => {
  if (Notification.permission !== 'denied') {
    Notification.requestPermission().then(function (permission) {
      if (permission === 'granted') {
        new Notification('Notifications enabled', {
          body: 'Thank you for enabling notifications.',
          icon: verseIcon,
        });
      }
    });
  }
};

export const sendWebNotification = (title: string, message: string) => {
  let hasPermission = checkWebNotificationPermission();
  // only display notification if tab user is on another tab
  if (hasPermission && document.visibilityState === 'hidden') {
    let notification = new Notification(title, { body: message, icon: verseIcon });

    notification.onclick = function () {
      window.parent.focus();
      this.close();
    };
  }

  if (!hasPermission) {
    requestWebNotificationPermission();
  }
};

export const throttle = (fn: Function, limit: number) => {
  let inThrottle: boolean;

  return function throttleWrapper(...args: any[]) {
    if (!inThrottle) {
      fn.apply(null, args);
      inThrottle = true;

      window.setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const debounce = (fn: Function, interval: number) => {
  let timeout: number;

  return function debounceWrapper(...args: any[]) {
    const debouncedFn = () => {
      timeout = -1;
      fn.apply(null, args);
    };

    clearTimeout(timeout);
    timeout = window.setTimeout(debouncedFn, interval);
  };
};

export const copyToClipboard = (text?: string): Promise<any> => {
  // navigator may not always have the clipboard property, need to prevent TypeScript from failing
  const navigator = window.navigator as any;

  // do we have the clipboard API?
  if (text && navigator && navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }

  return Promise.reject();
};
